import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  inject,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { AppConfigService } from '@core/index';
import {
  HOST,
  InjectionTokenIsWebComponent,
  IS_WEB_COMPONENT,
} from '@core/injection-tokens';
import { TypedDictionary } from '@core/utils';
import { WindowService } from '@core/window-actions';
import { moveFocus } from '@shared/booker';
import { booleanStringCheck } from '@shared/ui/utils/global-utils/boolean-string-check';
import { OriginsFacade } from '@store/origins/origins.facade';
import { Airport, CountryWithAirports } from '@store/origins/types';
import {
  JbAutocompleteComponent,
  JbAutocompleteOptionsGroup,
  JbDialogService,
} from 'jb-component-library';
import { equals, prop, sortBy } from 'ramda';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { CityPairSelector as CityPairSelectorContent } from '../city-pair-selector/types/city-pair-selector.type';
import { RegionSelectorV2DialogComponent } from '../region-selector-dialog-v2';
import { CitySelectorFuzzySearchService } from './fuzzy-search/city-selector-fuzzy-search.service';
import {
  CitySelectorState,
  CitySelectorStore,
  CitySelectorViewModel,
} from './store';
import { CitySelectorType } from './types/city-selector-type.enum';
import { airportInvalidCodeValidator } from './utils/city-selector.utils';

export enum CitySelectorSearchMode {
  fuzzyThenFilter = 1,
  fuzzyOnNoResults = 2,
  fuzzyOnlyThenSort = 3,
  defaultSearch = 4,
}

@Component({
  selector: 'dot-city-selector',
  templateUrl: './city-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CitySelectorStore],
})
export class CitySelectorComponent
  implements OnInit, AfterViewInit, ControlValueAccessor
{
  private _destroy = inject(DestroyRef);
  private _onChange: (_: any) => void;

  private prevItemCode: string;
  private selectNextInput: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );

  @Input() set content(content: CityPairSelectorContent) {
    this.store.patchState({ content });
  }
  @Input() set showShortCityName(showShortCityName: boolean) {
    this.store.patchState({ showShortCityName });
  }
  @Input() set separatePartnerCities(separatePartnerCities: boolean) {
    this.store.patchState({ separatePartnerCities });
  }
  @Input() set suggestions(airportSuggestions: CountryWithAirports[]) {
    this.store.patchState({ airportSuggestions });
  }
  @Input() set type(citySelectorType: CitySelectorType) {
    this.store.patchState({ citySelectorType });
  }
  @Input() set recentAirports(recentAirports: Airport[]) {
    this.store.patchState({ recentAirports });
  }
  @Input() set formWrapper(formWrapper: HTMLElement) {
    this.store.patchState({ formWrapper });
  }
  @Input() set filterOutPartner(filterOutPartner: boolean) {
    this.store.patchState({ filterOutPartner });
  }
  @Input() set hideLegend(hideLegend: boolean) {
    this.store.patchState({ hideLegend });
  }
  @Input() set customLegendMessage(customLegendMessage: string) {
    this.store.patchState({ customLegendMessage });
  }
  @Input() set moveFocusToNextInput(moveFocusToNextInput: boolean) {
    this.store.patchState({ moveFocusToNextInput });
  }

  @Input() enableFuzzySearch: boolean;

  // Do we really need it?
  // sometimes city-selector is contained inside of a dialog
  // @ObservableInput() parentDialog$: Observable<DialogTempWrapperComponent>;

  @Output() airportChange: EventEmitter<Airport> = new EventEmitter();
  @Output() userChangedAirport: EventEmitter<Airport> = new EventEmitter();

  @ViewChild('jbAutoComplete', { static: false })
  jbAutocompleteComponent: JbAutocompleteComponent;

  vm$: Observable<CitySelectorViewModel> = this.store.vm$;
  /** Value of airport selected from dropdown */
  airportControl: FormControl<Airport> = new FormControl();
  /** Filtered airports that appear in the dropdown */
  filteredAirports: Airport[] = [];
  searchMode: CitySelectorSearchMode = CitySelectorSearchMode.defaultSearch;

  openDialog = this.store.effect(event$ =>
    event$.pipe(
      takeUntilDestroyed(this._destroy),
      withLatestFrom(
        this.store.state$,
        this.store.groupedAirports$,
        this.store.label$,
        this.store.placeholder$,
      ),
      tap(
        ([_, state, groupedAirports, label, placeholder]: [
          any,
          CitySelectorState,
          JbAutocompleteOptionsGroup[],
          string,
          string,
        ]) => {
          const componentRef = this.jbDialogService.openDialog(
            RegionSelectorV2DialogComponent,
          );

          const countries = state.separatePartnerCities
            ? state.airportSuggestions
            : this.sortAirportsAlphabetically(state.airportSuggestions);

          componentRef.handleData(
            state.content,
            this.airportControl,
            state.originsFacadeRegions,
            countries,
            this.jbAutocompleteComponent?.wrapper?.nativeElement?.querySelector(
              'input',
            ),
            state.formWrapper,
            state.filterOutPartner,
            state.hideLegend,
            state.customLegendMessage,
            label,
            placeholder,
            state.isMobile,
            groupedAirports,
            state.separatePartnerCities,
          );
        },
      ),
    ),
  );

  constructor(
    @Optional()
    @Self()
    public ngControl: NgControl,
    public jbDialogService: JbDialogService,
    private windowService: WindowService,
    private originsFacade: OriginsFacade,
    public store: CitySelectorStore,
    private cdr: ChangeDetectorRef,
    private fuzzySearch: CitySelectorFuzzySearchService,
    private config: AppConfigService,
    @Inject(HOST) private host: string,
    @Inject(IS_WEB_COMPONENT)
    private isWebComponent: InjectionTokenIsWebComponent,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    this.store.patchState(
      this.windowService.isMobile.pipe(map(isMobile => ({ isMobile }))),
    );
    this.store.patchState(
      this.originsFacade.nearbyAirportCodes.pipe(
        map(nearbyAirportCodes => {
          return {
            nearbyAirportCodes,
          };
        }),
      ),
    );
    this.store.patchState(
      this.originsFacade.airports.pipe(
        map(originsFacadeAirports => ({ originsFacadeAirports })),
      ),
    );
    this.store.patchState(
      this.originsFacade.airportsMap.pipe(
        filter(Boolean),
        map((originsFacadeAirportsHashMap: TypedDictionary<Airport>) => ({
          originsFacadeAirportsHashMap,
        })),
      ),
    );
    this.store.patchState(
      this.originsFacade.regions.pipe(
        map(originsFacadeRegions => ({ originsFacadeRegions })),
      ),
    );
  }

  ngOnInit(): void {
    // If Airport selected through dialog, for some reason it fires twice
    this.airportControl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroy),
        distinctUntilChanged((prev, curr) => {
          return equals(prev, curr);
        }),
      )
      .subscribe(this._onChange);

    this.store.value$
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe(this._onChange);

    // default to defaultSearch if not enabled or searchMode not supplied
    this.searchMode = booleanStringCheck(this.enableFuzzySearch)
      ? this.config.bookerFuzzySearch.searchMode ||
        CitySelectorSearchMode.defaultSearch
      : CitySelectorSearchMode.defaultSearch;

    this.selectNextInput
      .pipe(
        takeUntilDestroyed(this._destroy),
        withLatestFrom(this.store.state$),
      )
      .subscribe(([selectNextInput, state]) => {
        if (!state.isMobile && selectNextInput && state.moveFocusToNextInput) {
          setTimeout(() =>
            moveFocus(
              state.formWrapper,
              this.jbAutocompleteComponent?.wrapper?.nativeElement?.querySelector(
                'input',
              ),
              true,
            ),
          );
        }
      });

    this.airportControl.valueChanges
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe(airport => this.store.setValue(airport));

    this.store.value$
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe(value => {
        this.airportControl.setValue(value, { emitEvent: false });
        this.cdr.detectChanges();
      });

    this.store.errorMessage$
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe(errorMessage => {
        this.setValidators(errorMessage);
      });

    this.store.isMobile$
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe(isMobile => {
        if (isMobile) {
          this.jbDialogService.closeDialog();
        }
      });
  }

  async ngAfterViewInit() {
    // Do not allow the user to input whitespaces in the begining of the city. REF: DOT-10326
    this.jbAutocompleteComponent.viewControl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroy),
        /*
        Check if text starts with whitespace and if there is value after the whitespace (In Case of Copy and Paste)..
        The user may copy a value whit white space in the beggining.. Case this happens, do not clear input.
        */
        filter(value => /^\s/.test(value) && value.trimStart().length < 1),
      )
      .subscribe(() => {
        this.jbAutocompleteComponent.clearInputValue();
      });

    // set up fuzzy when not using only cl search
    if (this.searchMode !== CitySelectorSearchMode.defaultSearch) {
      // await for all airports before proceeding to subscribe to query changes from auto-complete
      const allAirports = await firstValueFrom(this.store.allAirports$);
      this.fuzzySearch.initialize(allAirports);

      this.jbAutocompleteComponent.queryChange
        .pipe(
          takeUntilDestroyed(this._destroy),
          filter(query => query !== null && query !== undefined),
          debounceTime(this.config.bookerFuzzySearch?.debounceTime || 300),
        )
        .subscribe(query => {
          switch (this.searchMode) {
            case CitySelectorSearchMode.fuzzyThenFilter:
              this.filteredAirports = this.fuzzySearch.fuzzyThenFilter(query);
              break;
            case CitySelectorSearchMode.fuzzyOnNoResults:
              this.filteredAirports = this.fuzzySearch.fuzzyOnNoResults(query);
              break;
            case CitySelectorSearchMode.fuzzyOnlyThenSort:
              this.filteredAirports = this.fuzzySearch.fuzzyOnlyThenSort(query);
              break;
            // on onlyCLSearch it will use the cl searching and doesnt need a case in this switch
          }
          this.cdr.markForCheck();
        });
    }
  }

  sortAirportsAlphabetically(
    countries: CountryWithAirports[],
  ): CountryWithAirports[] {
    return countries?.map(country => ({
      ...country,
      airports: sortBy(prop('name'))(country.airports),
    }));
  }

  openDialogOrLinkInDropdownSelector(
    modalLinkData: CitySelectorViewModel['modalLinkData'],
    isMobile: boolean,
  ) {
    if (modalLinkData.shouldOpenDialog || isMobile) {
      this.openDialog();
      return;
    }

    if (!this.isWebComponent) {
      this.windowService.internalNavigate(modalLinkData.url);
    } else {
      let url = modalLinkData.url;
      if (url.startsWith('/')) {
        url = `${this.host}${modalLinkData.url}`;
      }
      this.windowService.navigateToOutsideUrl(url);
    }
  }

  setValidators(message: string): void {
    // adding validator to outer form control
    if (this.ngControl) {
      const validators = this.ngControl.control.validator
        ? [
            airportInvalidCodeValidator(message),
            this.ngControl.control.validator,
          ]
        : [airportInvalidCodeValidator(message)];

      this.ngControl.control.setValidators(validators);
      this.ngControl.control.updateValueAndValidity();
    }
  }

  handleOptionSelected(selectedItem: Airport): void {
    if (!!selectedItem && selectedItem.code !== this.prevItemCode) {
      if (this.jbAutocompleteComponent?.inputFocused) {
        this.selectNextInput.next(true);
        this.userChangedAirport.emit(selectedItem);
      } else {
        this.airportChange.emit(selectedItem);
      }
      this.prevItemCode = selectedItem.code;
    }

    if (!selectedItem) {
      this.prevItemCode = '';
      if (!!this.prevItemCode) {
        this.jbAutocompleteComponent?.clearInputValue();
      }
    }
  }

  registerOnChange(fn: (value: Airport) => void): void {
    this._onChange = fn;
  }

  writeValue(value: Airport): void {
    this.store.setValue(value);
  }

  registerOnTouched(fn: any): void {
    return;
  }

  focusOnInput() {
    this.jbAutocompleteComponent.input.handleFocus({} as any);
    this.jbAutocompleteComponent.input.input.nativeElement.focus();
  }
}
